import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import { PaymentAccountState } from 'typings/reduxStore'
import { PaymentMethodsDetails, PaymentMethodsResponse } from 'typings/payment'
import { getErrorMessage } from 'helpers/parseError'
import { ServerError } from 'typings/serverError'
import responseCodes from 'const/responseCodes'
import { OriginalRootState } from 'redux/rootReducer'
import {
  addPaymentAccount,
  deletePaymentAccount,
  getPaymentAccountList
} from './async-thunks'

export const initState: PaymentAccountState = {
  isDataLoading: true,
  errorMessage: '',
  gridOption: {
    limit: '20',
    currentPage: 1
  },
  dataList: [] as Array<PaymentMethodsDetails>,
  filterOption: { value: '', label: '' },
  isAccountDeleted: false,
  addAccountStatus: '',
  addedAccount: {
    accountNumber: '',
    routingNumber: '',
    nickname: ''
  },
  isPaymentScheduled: false
}

export const paymentAccountSlice = createSlice({
  name: 'paymentAccountSlice',
  initialState: initState,
  reducers: {
    addPaymentAccountFailure: state => {
      return {
        ...state,
        addAccountStatus: 'error'
      }
    },
    addPaymentAccountSuccess: (state, action) => {
      return {
        ...state,
        addAccountStatus: 'success',
        addedAccount: {
          accountNumber: action.payload.accountNumber,
          routingNumber: action.payload.routingNumber,
          nickname: action.payload.nickname
        }
      }
    },
    addPaymentAccountReset: state => {
      return {
        ...state,
        addAccountStatus: '',
        addedAccount: {
          accountNumber: '',
          routingNumber: '',
          nickname: ''
        }
      }
    },
    updatePaymentAccountState: (state, action) => {
      return {
        ...state,
        ...action.payload
      }
    },
    resetError: state => {
      state.isAccountDeleted = false
      state.addAccountStatus = ''
      state.isPaymentScheduled = false
      state.errorMessage = ''
    }
  },
  extraReducers: builder => {
    builder
      .addCase(
        addPaymentAccount.fulfilled,
        (state: PaymentAccountState, action) => {
          state.addAccountStatus = 'success'
          state.isDataLoading = false
          state.addedAccount = {
            accountNumber: action.meta.arg.accountNumber,
            routingNumber: action.meta.arg.routingNumber,
            nickname: action.meta.arg?.nickname
          }
        }
      )
      .addCase(addPaymentAccount.pending, (state: PaymentAccountState) => {
        state.isDataLoading = true
      })
      .addCase(
        addPaymentAccount.rejected,
        (state: PaymentAccountState, action: PayloadAction<unknown>) => {
          state.errorMessage = getErrorMessage(action.payload as ServerError)
          state.isDataLoading = false
        }
      )
      .addCase(
        getPaymentAccountList.fulfilled,
        (
          state: PaymentAccountState,
          action: PayloadAction<PaymentMethodsResponse>
        ) => {
          state.isDataLoading = false
          state.errorMessage = ''
          state.dataList = action.payload.paymentMethods
        }
      )
      .addCase(
        getPaymentAccountList.rejected,
        (state: PaymentAccountState, action: PayloadAction<unknown>) => {
          state.dataList = []
          state.isDataLoading = false
          state.errorMessage = getErrorMessage(action.payload as ServerError)
        }
      )
      .addCase(deletePaymentAccount.fulfilled, (state: PaymentAccountState) => {
        state.isAccountDeleted = true
        state.errorMessage = ''
        state.isDataLoading = false
      })
      .addCase(
        deletePaymentAccount.rejected,
        (state: PaymentAccountState, action: PayloadAction<unknown>) => {
          const { statusCode, businessErrorCode } =
            action.payload as ServerError

          state.isAccountDeleted = false
          state.isPaymentScheduled =
            businessErrorCode === responseCodes.paymentScheduled
          state.isPaymentAutopay =
            statusCode === 422 &&
            businessErrorCode === responseCodes.paymentMethodExistsAutopay
          state.isDataLoading = false
        }
      )
  }
})

const selectSelf = (state: OriginalRootState) =>
  state.paymentAccount as PaymentAccountState

export const selectIsDataLoading = createSelector(
  selectSelf,
  state => state.isDataLoading
)

export const {
  addPaymentAccountFailure,
  addPaymentAccountSuccess,
  addPaymentAccountReset,
  updatePaymentAccountState,
  resetError
} = paymentAccountSlice.actions

export default paymentAccountSlice.reducer
